import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Psychology as PsychologyIcon,
  ViewKanban as ViewKanbanIcon,
  EventNote as EventNoteIcon,
  Groups as GroupsIcon,
  Timer as TimerIcon,
  Analytics as AnalyticsIcon,
  IntegrationInstructions as IntegrationIcon,
} from '@mui/icons-material';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

const Features: React.FC = () => {
  const theme = useTheme();

  const features: Feature[] = [
    {
      icon: <ViewKanbanIcon sx={{ fontSize: 40 }} />,
      title: 'Intelligent Kanban Boards',
      description:
        'Drag-and-drop interface with custom columns and workflows. Visualize your work and optimize flow with our intuitive board system.',
      color: theme.palette.primary.main,
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Estimation',
      description:
        'Machine learning-based story point estimation using historical data. Get accurate estimates in seconds and improve planning accuracy by 40%.',
      color: theme.palette.secondary.main,
    },
    {
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      title: 'Leadership Dashboard',
      description:
        'Real-time metrics, KPIs, and risk assessment at your fingertips. Make data-driven decisions with comprehensive insights into team performance.',
      color: theme.palette.success.main,
    },
    {
      icon: <EventNoteIcon sx={{ fontSize: 40 }} />,
      title: 'Sprint Planning',
      description:
        'Capacity planning, velocity tracking, and burndown charts. Plan sprints efficiently with intelligent workload distribution.',
      color: theme.palette.info.main,
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
      title: 'Team Collaboration',
      description:
        'Real-time updates, comments, mentions, and notifications. Keep everyone aligned with seamless communication tools.',
      color: theme.palette.warning.main,
    },
    {
      icon: <TimerIcon sx={{ fontSize: 40 }} />,
      title: 'Time Tracking',
      description:
        'Integrated timesheets and productivity analytics. Track time spent on tasks and gain insights into team efficiency.',
      color: theme.palette.error.main,
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      title: 'Advanced Analytics',
      description:
        'Burndown charts, velocity tracking, and custom reports. Visualize progress and identify bottlenecks with powerful analytics.',
      color: theme.palette.primary.dark,
    },
    {
      icon: <IntegrationIcon sx={{ fontSize: 40 }} />,
      title: 'Seamless Integrations',
      description:
        'Import from Jira, MS Planner, and export reports. Migrate your data effortlessly and connect with your favorite tools.',
      color: theme.palette.secondary.dark,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(circle at 2px 2px, ${theme.palette.primary.main} 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          {/* Section Badge */}
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.875rem',
            }}
          >
            POWERFUL FEATURES
          </Typography>

          {/* Section Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              maxWidth: '700px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Everything You Need to Deliver Faster
          </Typography>

          {/* Section Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            Modern tools designed for agile teams. From planning to delivery, we've got you
            covered.
          </Typography>
        </Stack>

        {/* Features Grid */}
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 40px ${alpha(feature.color, 0.2)}`,
                    borderColor: alpha(feature.color, 0.3),
                    '& .feature-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                      color: feature.color,
                    },
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    {/* Icon */}
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(feature.color, 0.1),
                        color: alpha(feature.color, 0.8),
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {feature.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {feature.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            Want to see these features in action?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-block',
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              pb: 0.5,
              transition: 'all 0.3s ease',
              '&:hover': {
                color: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
              },
            }}
          >
            Explore Interactive Demo →
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Features;

